import { api } from '@api'
import { useQuery } from '@tanstack/react-query'
import { secureTokenStore } from '@store'

/**
 * The free-tier configuration published by the API (PR #536, `GET /plan-limits`).
 * Used for proactive UX + copy; the hard gate stays server-side (402 → paywall).
 */
export interface PlanLimits {
  freeMaxContacts: number
  freeMaxGhostProfiles: number
  freeMaxNotes: number
  freeKeepContactEnabled: boolean
  freeMaxKeepContactReminders: number
  freeCardScanEnabled: boolean
  freeCardScanMonthlyQuota: number
  exportPremiumOnly: boolean
}

// Conservative defaults: assume the tightest free tier until the API answers,
// so we never accidentally imply a feature is unlimited.
const DEFAULT_PLAN_LIMITS: PlanLimits = {
  freeMaxContacts: 0,
  freeMaxGhostProfiles: 0,
  freeMaxNotes: 0,
  freeKeepContactEnabled: false,
  freeMaxKeepContactReminders: 0,
  freeCardScanEnabled: false,
  freeCardScanMonthlyQuota: 0,
  exportPremiumOnly: true,
}

// An answer without these two limits comes from a server that applies its
// built-in ones: show those rather than 0.
const SERVER_DEFAULT_MAX_GHOST_PROFILES = 30
const SERVER_DEFAULT_MAX_NOTES = 10

// Plan limits are effectively static config — they change on a server deploy,
// not per user action. A long stale window avoids needless refetches.
const PLAN_LIMITS_STALE_TIME_MS = 30 * 60 * 1000

export const PLAN_LIMITS_QUERY_KEY = ['plan-limits'] as const

const fetchPlanLimits = async (): Promise<PlanLimits> => {
  const { data } = await api.get<Partial<PlanLimits>>('/plan-limits')
  return {
    freeMaxContacts: Number(data?.freeMaxContacts ?? DEFAULT_PLAN_LIMITS.freeMaxContacts),
    freeMaxGhostProfiles: Number(data?.freeMaxGhostProfiles ?? SERVER_DEFAULT_MAX_GHOST_PROFILES),
    freeMaxNotes: Number(data?.freeMaxNotes ?? SERVER_DEFAULT_MAX_NOTES),
    freeKeepContactEnabled: Boolean(
      data?.freeKeepContactEnabled ?? DEFAULT_PLAN_LIMITS.freeKeepContactEnabled,
    ),
    freeMaxKeepContactReminders: Number(
      data?.freeMaxKeepContactReminders ?? DEFAULT_PLAN_LIMITS.freeMaxKeepContactReminders,
    ),
    freeCardScanEnabled: Boolean(
      data?.freeCardScanEnabled ?? DEFAULT_PLAN_LIMITS.freeCardScanEnabled,
    ),
    freeCardScanMonthlyQuota: Number(
      data?.freeCardScanMonthlyQuota ?? DEFAULT_PLAN_LIMITS.freeCardScanMonthlyQuota,
    ),
    exportPremiumOnly: Boolean(data?.exportPremiumOnly ?? DEFAULT_PLAN_LIMITS.exportPremiumOnly),
  }
}

/**
 * The active free-tier plan limits. Runs only when authenticated (guarded on
 * the access token, like other authed reads). Returns conservative defaults
 * until the API answers.
 */
export const usePlanLimits = () => {
  const isAuthenticated = !!secureTokenStore.getAccessToken()

  const query = useQuery<PlanLimits>({
    queryKey: PLAN_LIMITS_QUERY_KEY,
    queryFn: fetchPlanLimits,
    enabled: isAuthenticated,
    staleTime: PLAN_LIMITS_STALE_TIME_MS,
  })

  return {
    planLimits: query.data ?? DEFAULT_PLAN_LIMITS,
    isLoading: query.isLoading,
    // False while `planLimits` still holds the defaults above.
    isLoaded: query.data !== undefined,
    refetch: query.refetch,
  }
}
